import React, { useMemo } from 'react';
import { Avatar } from '@shoflo/polymer/components/Avatar/Avatar';
import { RoleHelper } from '@shoflo/polymer/helpers/RoleHelper/RoleHelper';
import { shouldHaveFeature } from '@shoflo/polymer/helpers/Feature/Feature';
import DropdownMultiSelect from 'components/DropdownMultiSelect';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import _ from 'underscore';
import roles from 'constants/Roles';
import { AVAILABLE_TOOLS } from 'constants/AvailableTools';
import CrewRolePicker from 'components/event/CrewRolePicker';
import CrewListItemMenu from 'components/event/CrewListItemMenu';
import { getAvailableToolsLimit, getEventRole } from 'utils/teamHelper';

const invertedRoles = _.invert(roles);

const StyledTD = styled.td`
  border-top: none !important;
  border-bottom: 2px solid #f0f2f6;
  vertical-align: middle !important;
  padding: 1rem 0 !important;
`;

const UserWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledUserName = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #333333;
  margin-left: 55px;

  @media only screen and (max-width: 768px) {
    margin-left: 10px;
  }
`;

const StyledLinkText = styled.div`
  font-size: 16px !important;
  color: #5693f0 !important;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
`;

const StyledEm = styled.div`
  font-size: 14px;
  line-height: 16px;
  color: #767676;
`;

const StyledSubText = styled(StyledEm)`
  margin-left: 55px;

  @media only screen and (max-width: 768px) {
    display: none;
    margin-left: 10px;
  }
`;

const StyledCrewSettings = styled(StyledTD)`
  text-align: right;

  .dropdown-menu.show {
    width: 200px;
    left: auto !important;
    right: 0;
    transform: translate3d(0px, 34px, 0px) !important;
  }
`;

const StyledToolsLabel = styled.div`
  display: inline-block;
  text-align: center;
  background: #f0f2f6;
  color: #33373d;
  padding: 5px 10px;
  margin-right: 5px;
  border: 1px solid #dfe3ec;
  border-radius: 2px;
`;

const CrewListItem = ({
  teamId,
  eventId,
  user,
  canManage,
  onDeleteClick,
  onChangeRoleClick,
  onResendInviteClick,
  onAvailableToolsChanged,
  team,
  isLoggedInTeamAdmin,
  isLoggedInEventAdmin
}) => {
  const {
    role: roleValue,
    _id: roleId,
    availableTools,
  } = getEventRole(eventId, user);
  const readOnlyRoleTitle = invertedRoles[roleValue];

  const availableToolsLimit = useMemo(() => {
    if (team) {
      return getAvailableToolsLimit(team);
    }
    return {};
  }, [team]);

  const isTeamAdmin = RoleHelper.hasTeamAdminRole(user, teamId);
  let dropdownOptions = [];
  let isToolsAvailable = false;

  if (availableTools) {
    const computedDropdownOptions = Object.entries(AVAILABLE_TOOLS).map(
      (tool) => {
        const currentTool = availableToolsLimit[tool[1]] || {
          limit: 0,
          assignedUsers: 0,
        };
        if (currentTool.limit) isToolsAvailable = true;
        const toolDescription = currentTool.limit
          ? `(${currentTool.assignedUsers} of ${currentTool.limit})`
          : '(Unavailable)';
        return {
          checked: availableTools.includes(tool[0]),
          id: `${tool[1]}-${user._id}`,
          name: tool[1],
          label: `${tool[1]} ${
            isLoggedInTeamAdmin && isLoggedInEventAdmin ? toolDescription : ''
          }`,
          value: tool[0],
          disabled: !currentTool.limit,
        };
      },
    );

    dropdownOptions = computedDropdownOptions;
  }

  const getAvailableTools = () => {
    return availableTools.map((tool) => (
      <StyledToolsLabel key={`${user._id}_${tool}`}>
        {AVAILABLE_TOOLS[tool]}
      </StyledToolsLabel>
    ));
  };

  const handleDropdownItemSelect = (event) => {
    const toggledTool = event.target.value;
    let updatedAvailableTools;
    if (availableTools.includes(toggledTool)) {
      updatedAvailableTools = availableTools.filter(
        (availableTool) => availableTool !== toggledTool,
      );
    } else {
      updatedAvailableTools = [...availableTools, toggledTool];
    }

    onAvailableToolsChanged(user, updatedAvailableTools, toggledTool);
  };

  return (
    <tr data-testid={'crewRow-' + user.firstName + user.lastName}>
      <StyledTD>
        <UserWrapper>
          <Avatar user={user} />
          <div className="dd-privacy-mask">
            <StyledUserName
              data-testid={'crewName-' + user.firstName + user.lastName}
            >
              {user.firstName} {user.lastName}
            </StyledUserName>
            <StyledSubText>{user.email}</StyledSubText>
          </div>
        </UserWrapper>
      </StyledTD>
      <StyledTD>
        {canManage ? (
          <CrewRolePicker
            value={roleValue}
            userId={user._id}
            onChange={onChangeRoleClick.bind(null, roleId, user._id)}
          />
        ) : (
          readOnlyRoleTitle
        )}
      </StyledTD>

      {shouldHaveFeature('event-crew-tools') && (
        <StyledTD>
          {availableTools && getAvailableTools()}
          <DropdownMultiSelect
            type="checkbox"
            srMessage=""
            onClick={handleDropdownItemSelect}
            options={dropdownOptions}
            id={`product-select-${user._id}`}
          />
        </StyledTD>
      )}

      <StyledCrewSettings
        data-testid={`crewDropdown-` + user.email}
        className="text-end d-none d-lg-table-cell"
      >
        {canManage && (user.pending || isTeamAdmin !== true) ? (
          <CrewListItemMenu
            showRemove={isTeamAdmin !== true}
            showResend={user.pending}
            onResend={onResendInviteClick.bind(null, user._id)}
            onDelete={onDeleteClick.bind(null, user._id)}
          />
        ) : (
          isTeamAdmin && (
            <StyledSubText data-testid="crewRole">Team Admin</StyledSubText>
          )
        )}
      </StyledCrewSettings>
    </tr>
  );
};

CrewListItem.propTypes = {
  teamId: PropTypes.string.isRequired,
  eventId: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  canManage: PropTypes.bool.isRequired,
  onChangeRoleClick: PropTypes.func,
  onResendInviteClick: PropTypes.func,
  onDeleteClick: PropTypes.func.isRequired,
  onAvailableToolsChanged: PropTypes.func,
  team: PropTypes.object.isRequired,
  isLoggedInTeamAdmin: PropTypes.bool.isRequired,
  isLoggedInEventAdmin: PropTypes.bool.isRequired,
  showModal: PropTypes.func.isRequired,
};

export default CrewListItem;
